import styles from '../../style.module.scss';
import { useContext, useEffect, useState } from 'react';
import { ServiceRequestContext } from '@/context/serviceRequestContext';
import { AlertModal } from '@/globalComponents/alertModal';
import { address } from '.';
import { ProductContext } from '@/context/productContext';
import { bookService } from '@/actions/serviceRequestSummary';
import { AssetsData } from '@/types/asset';
import ServiceRequestInfo from '../../SubComponents/serviceRequestInfo';
import { useRouter } from 'next/router';
import { rescheduleServiceRequest } from '@/actions/rescheduleServiceRequest';
import { AppScreens, WebEngageEventData, WebEngageEventNames } from '@/types/wegengage';
import { WebEngageContext } from '@/context/webengageContext';
import WebEngageUtils from '@/utils/webengageUtils';
import { FullPageLoader } from '../../SubComponents/FullPageLoader';
import WarningIcon from '@/../public/images/icons/largeWarningIcon.svg';
import { GTMEventData, GTMEventNames, GTMPageNames } from '@/types/gtm';
import { ProfileContext } from '@/context/profileContext';
import { getGACookieId } from '@/utils/gaUtils';
import AppBar from '@/shared/AppBar';
import Button from '@/shared/Button';
import FixedWrapper from '@/shared/FixedWrapper';
import ProductBanner from '@/globalComponents/productBanner';
import Div from '@/shared/Div';
import Span from '@/shared/Span';
import { hasQueryParam, removeQueryParam } from '@/utils/url';

interface ServiceRequestSummaryProps {
  defaultAddress: address;
  productsData: AssetsData[];
}

const ServiceRequestSummary = (props: ServiceRequestSummaryProps) => {
  const { defaultAddress } = props;
  const { state: servicetState, dispatch: serviceAction } = useContext(ServiceRequestContext);
  const {
    serviceType,
    defectType,
    addnRemarks,
    serviceDate,
    serviceSlot,
    address,
    serviceRequestID,
    webengageData,
    isRescheduleFlow,
    incidentId,
    isFMS,
    incidentType,
    componentId,
    customerCode,
    rescheduleReasonCode,
    rescheduleDescription,
  } = servicetState;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorDescription, setErrorDescription] = useState<string>('');
  const [shouldShowRetryButton, setShouldShowRetryButton] = useState<
    'retryCTA' | 'slotCTA' | 'homeCTA'
  >('retryCTA');
  const { state: webengageState } = useContext(WebEngageContext);
  const { state: productState } = useContext(ProductContext);
  const { state: profileState } = useContext(ProfileContext);

  const selProduct = productState?.selectedProduct;
  const valueClassNames = `block text-base md:text-xl ${styles.value}`;

  const [isDoneButtonLoading, setIsDoneButtonLoading] = useState<boolean>(false);

  const _webengageObj: WebEngageEventData = {
    ...webengageState.webengageDefault,
    navigated_from: AppScreens.SERVICE_REQUEST_SUMMARY_SCREEN,
    customer_type: WebEngageUtils.stringUtil(selProduct?.expiryStatus?.status),
    mandatory_service_open: webengageData?.mandatory_service_open,
    service_request_open: webengageData?.service_request_open,
    scheduling_automation_flow: webengageData?.scheduling_automation_flow,
    product_selected: WebEngageUtils.stringUtil(webengageData?.product_selected),
    product_id: WebEngageUtils.stringUtil(webengageData?.product_id),
    service_type: WebEngageUtils.stringUtil(webengageData?.service_type),
    defect_selected: WebEngageUtils.stringUtil(webengageData?.defect_selected),
    date_selection_method: webengageData?.date_selection_method,
    reschedule: WebEngageUtils.returnBoolString(isRescheduleFlow === true),
    available_slots: WebEngageUtils.stringUtil(webengageData?.available_slots),
    current_time_vs_earliest_available_slot: webengageData?.current_time_vs_earliest_available_slot,
    time_slot_selected: WebEngageUtils.stringUtil(webengageData?.time_slot_selected),
    date_selected: WebEngageUtils.stringUtil(webengageData?.date_selected),
  };

  useEffect(() => {
    if (isSuccess) {
      serviceAction.changeComponent('bookServiceSuccess');
    }
  }, [isSuccess]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const _we = window.webengage;
    if (_we) {
      _we.track(WebEngageEventNames.SERVICE_REQ_SUMMARY_SCREEN_OPENED, _webengageObj);
    }
  }, []);

  const getServiceRequestType = () => {
    if (serviceType === 'repair') {
      // istanbul ignore next
      return 'Repair';
    }
    if (serviceType === 'uninstall') {
      // istanbul ignore next
      return 'Uninstall';
    }
    return 'Reinstall';
  };

  const pushGAEvent = async () => {
    if (window.dataLayer) {
      const { name, address, customerCode } = selProduct;
      const { city, postal_code } = address;
      const gaCookieValue = getGACookieId();
      const eventData: GTMEventData = {
        event: GTMEventNames.SERVICE_REQ_SUBMITTED,
        pageName: GTMPageNames.SERVICE_THANKYOU_PAGE,
        loginStatus: 'LoggedIn',
        siteSection: 'Service',
        supportCategory: 'Service',
        buttonName: 'SUBMIT',
        serviceCategory: getServiceRequestType(),
        serviceType: defectType,
        serviceDate: serviceDate?.toString(),
        mid: profileState.hashed_mobile_number,
        eid: '',
        userPincode: postal_code,
        customerId: customerCode,
        Usercity: city,
        GAcookie_id: gaCookieValue,
        navigated_from: 'Book a slot',
        serviceTime: serviceSlot?.slot.startTime?.toString().split(' ')[4],
        serviceProductName: name,
      };
      if (isFMS) {
        eventData.isFMS = true;
      }

      window.dataLayer.push(eventData);
    }
  };

  const toggleSuccessModal = async () => {
    setIsLoading(true);
    let bookServiceFlow = false;
    try {
      let fetchedData;
      if (serviceRequestID === '') {
        const _we = window.webengage;
        if (_we) {
          _we.track(WebEngageEventNames.SERVICE_REQ_SUMMARY_SCREEN_NEXT_CLICKED, _webengageObj);
        }
        bookServiceFlow = true;
        let bookServicePayload: any = {
          serviceType,
          defectType,
          addnRemarks,
          serviceDate,
          serviceSlot,
          address: address,
          incidentId,
        };

        if (isFMS) {
          bookServicePayload = {
            ...bookServicePayload,
            isFMS: true,
            incidentId: '08099d1a-02ca-ec11-a7b5-000d3ac9df54',
            incidentType: incidentType,
            serviceType: 'REPAIR',
            customerCode: customerCode,
            componentId: componentId,
          };
        }

        const { data } = await bookService(selProduct, bookServicePayload);

        fetchedData = data;
        serviceAction.setServiceRequestID(fetchedData?.data?.ticketNumber);
        pushGAEvent();
      } else {
        await rescheduleServiceRequest(
          selProduct,
          {
            serviceType,
            serviceDate,
            serviceSlot,
            serviceReqID: serviceRequestID,
          },
          rescheduleReasonCode,
          rescheduleDescription,
        );
      }
      setIsSuccess(!isSuccess);
    } catch (error: any) {
      let errorMessage = '';
      const { response = '' } = error;
      const { data = '', status } = response;
      const { message = '', error: errorMsg } = data;

      if (bookServiceFlow) {
        if (status === 409) {
          setShouldShowRetryButton('homeCTA');
        }
        setErrorDescription(errorMsg);
      } else {
        if (error?.response?.status === 400) {
          error.response.data?.map((err: { message: string }, index: number) => {
            errorMessage += err.message;
            if (index !== error.response.data.length - 1) {
              errorMessage += ', ';
            }
          });
          setErrorDescription(errorMessage);
          setShouldShowRetryButton('retryCTA');
        } else if (error?.response?.status === 500) {
          if (error?.response.data.error === 'Resource id is busy') {
            setErrorDescription('The slot you have selected is no more available');
            setShouldShowRetryButton('slotCTA');
          } else if (message) {
            setErrorDescription(message);
            setShouldShowRetryButton('homeCTA');
          } else {
            setErrorDescription(errorMessage);
            setShouldShowRetryButton('retryCTA');
          }
        } else {
          setShouldShowRetryButton('retryCTA');
        }
      }

      setIsError(!isError);
    } finally {
      setIsLoading(false);
    }
  };

  const router = useRouter();

  const onRetryButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setIsError(false);
  };

  const onChangeSlotButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    serviceAction.changeComponent('bookASlot');
  };

  let _btnText = 'Retry';
  switch (shouldShowRetryButton) {
    case 'retryCTA':
      _btnText = 'Retry';
      break;
    case 'homeCTA':
      _btnText = 'Back to Service Requests';
      break;
    case 'slotCTA':
      _btnText = 'Change Slot';
      break;
  }

  let alertTitleText = 'Something went wrong';
  switch (shouldShowRetryButton) {
    case 'retryCTA':
      alertTitleText = 'Something went wrong';
      break;
    case 'homeCTA':
      alertTitleText = 'Service request already exist';
      break;
    case 'slotCTA':
      alertTitleText = 'Slot is not available now';
      break;
  }

  let alertIcon = null;
  switch (shouldShowRetryButton) {
    case 'retryCTA':
      alertIcon = null;
      break;
    case 'homeCTA':
    case 'slotCTA':
      alertIcon = WarningIcon;
      break;
  }

  const doneBtnClick = (e: any) => {
    switch (shouldShowRetryButton) {
      case 'retryCTA': {
        return onRetryButtonClick(e);
      }
      case 'homeCTA': {
        router.reload();
        return;
      }
      case 'slotCTA':
        return onChangeSlotButtonClick(e);
    }
  };

  const handleBackButton = () => {
    const MAP_QUERY_PARAM = 'map';

    if (hasQueryParam(MAP_QUERY_PARAM)) {
      removeQueryParam(MAP_QUERY_PARAM);
      serviceAction.changeComponent('mapLocator');
    } else {
      serviceAction.changeComponent('bookASlot');
      serviceAction.updateServiceDate(new Date());
    }
  };

  return (
    <section className="flex flex-col gap-5 -mt-8">
      <AppBar transparent fixed label="Service Request Summary" onClick={handleBackButton} />
      <Div className="pt-16">
        <ProductBanner
          deviceDetails={selProduct}
          showSelection={true}
          productTitleClasses="w-[85%] font-bold text-[0.875rem] md:text-[1.375rem] leading-[1.125rem] md:leading-[1.875rem]"
          badgeClasses="font-black md:font-extrabold text-[0.5rem] md:text-xs leading-[0.625rem]"
          productLocationClasses="font-normal text-xs md:text-lg leading-[0.875rem] text-[#828282]"
          cardClasses="mt-1 md:mt-4 py-4 px-2 md:pt-[1.3125rem] md:pb-[0.8125rem] md:px-8 border-[0.5px] md:border-0 border-[#4F4F4F]"
        />
      </Div>
      <ServiceRequestInfo
        listClassNames={`grid grid-cols-1 gap-s5 text-sm font-normal md:grid-cols-2 md:gap-10 md:text-lg md:w-[80%] md:mx-auto ${styles.key}`}
        defaultAddress={defaultAddress}
        valueClassNames={valueClassNames}
      />
      <Span className="md:max-w-sm self-center w-full hidden md:inline-block">
        <Button
          theme="primary"
          onClick={toggleSuccessModal}
          dataTestId="service-request-submit-desktop"
          disabled={isLoading}
          isLoading={isLoading}
          className="w-full "
        >
          {isRescheduleFlow === '' ? 'SUBMIT' : 'RESCHEDULE REQUEST'}
        </Button>
      </Span>

      <FixedWrapper className="p-6 pt-4 md:pb-4 md:hidden bg-white shadow-subtle">
        <Button
          theme="primary"
          onClick={toggleSuccessModal}
          dataTestId="service-request-submit-mobile"
          disabled={isLoading}
          isLoading={isLoading}
          className="w-full md:max-w-sm self-center"
        >
          {isRescheduleFlow === '' ? 'SUBMIT' : 'RESCHEDULE REQUEST'}
        </Button>
      </FixedWrapper>
      <AlertModal
        data-testid="errorModal"
        open={isError}
        title={alertTitleText}
        description_1={errorDescription}
        buttonText={_btnText}
        onButtonClick={doneBtnClick}
        severity={'error'}
        onClose={() => setIsError(false)}
        showCloseButton={true}
        titleClassNames="md:pr-[131px] md:pl-[132px]"
        icon={alertIcon}
      />
      <FullPageLoader isLoading={isLoading} />
    </section>
  );
};

export default ServiceRequestSummary;
